/**
 * Renders the actual HTML + CSS for a single toast notification
 * (mostly by delegating to toast-package).
 */
import type {CSSProperties} from "aphrodite";
import * as React from "react";

import {SchedulePolicy, useTimeout} from "@khanacademy/wonder-blocks-timing";

import {ErrorToast} from "./error-toast.tsx";
import {
    getToastLiveRegion,
    updateToastLiveRegion,
} from "./toast-live-region.ts";
import {Toast} from "./toast.tsx";

interface Props {
    // The content of the toast notification
    children?: React.ReactNode;
    // You can include a standardized link at the bottom of the toast
    linkText?: string;
    // The HREF for the link
    href?: string;
    // Time in ms when to automatically close the toast notification
    autoTimeout?: number;
    // Is this an error toast?
    isError?: boolean;
    // An optional test id for use in tests
    testId?: string;
    // Styles for toast container.
    style?: CSSProperties;
    // Color for close icon.
    closeIconColor?: string;
}

export const BaseToastNotification = (props: Props) => {
    const {
        children,
        linkText,
        href,
        autoTimeout = 6000, // 6s recommended for better a11y by https://sheribyrnehaber.medium.com/designing-toast-messages-for-accessibility-fb610ac364be
        isError = false,
        testId,
        style,
        closeIconColor,
    } = props;

    const [show, setShow] = React.useState(true);
    const [liveRegionNode, setLiveRegionNode] =
        React.useState<HTMLElement | null>(null);

    const timeout = useTimeout(
        () => {
            if (liveRegionNode) {
                const region = getToastLiveRegion();
                // @ts-expect-error - TS2345 - Argument of type 'Node' is not assignable to parameter of type 'HTMLElement'.
                updateToastLiveRegion(region, liveRegionNode.cloneNode(true));
            }
        },
        250,
        {schedulePolicy: SchedulePolicy.OnDemand},
    );

    const updateLiveRegion = React.useCallback(
        (node?: HTMLElement | null) => {
            if (!node) {
                return;
            }

            // JH - this live region works on Talkback/Android/Chrome and
            // JAWS/Windows/Chrome. When a dialog is open is does not work at all on
            // VoiceOver/Safari/[either iOS or OSX]. When a dialog is not open it
            // works on VoiceOver/Safari/OSX as well.
            //
            // Do not test on OSX outside of Safari - there is some weirdness but this
            // is not a setup we support
            //
            // How to test with the assignment complete notification
            //
            // * As a student, open an assigned article
            // * Wait for the notification to appear (sometimes this takes a moment)
            // * Listen for the message to be read to you. Because it's polite the
            // screen reader should finish reading what it's currently reading and
            // then read the message when there is a pause
            setLiveRegionNode(node);
            // update the live region on a delay. 250ms is what I tested first and
            // it worked (test cycles are slow, see above). Tried using an
            // animation frame, tested only on TalkBack/Android and the message was
            // not read
            timeout.set();
        },
        [timeout],
    );

    if (!show) {
        return null;
    }

    return isError ? (
        <ErrorToast
            linkText={linkText}
            href={href}
            onClose={() => setShow(false)}
            testId={testId}
            style={style}
            closeIconColor={closeIconColor}
        >
            {children}
        </ErrorToast>
    ) : (
        <Toast
            linkText={linkText}
            href={href}
            onClose={() => setShow(false)}
            autoTimeout={autoTimeout}
            testId={testId}
            style={style}
            closeIconColor={closeIconColor}
        >
            <div
                ref={(node) => {
                    updateLiveRegion(node);
                }}
            >
                {children}
            </div>
        </Toast>
    );
};
